import { Callout, CalloutTitle } from '../components/callout'
import type { Route } from './+types/privacy'

export const meta = (): Route.MetaDescriptors => [
  { title: 'Privacy | Vital Care Finder' },
  {
    name: 'description',
    content:
      'How Vital handles the information you give us to find a care provider: what we collect, how long we keep it, your rights, and the Consumer Health Data Notice covering health information you upload.',
  },
]

export const headers: Route.HeadersFunction = () => ({
  'Cache-Control': 'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400',
})

const email = (
  <a className="underline" href="mailto:privacy@vit.al">
    privacy@vit.al
  </a>
)

export default function Privacy() {
  return (
    <>
      <header className="space-y-3">
        <h1>Privacy Policy</h1>
        <p>30 July 2026</p>
        <p>Vital Software Inc.</p>
      </header>
      <Callout>
        <CalloutTitle level="2">Our privacy commitments</CalloutTitle>
        <p>This summary is for your convenience; the full policy below controls.</p>
        <ul>
          <li>We aim to collect only what we need to help you find and contact a provider.</li>
          <li>We do not sell your personal information, and we do not sell your health data in any form.</li>
          <li>We do not put advertising or analytics trackers on pages where you upload or view health information.</li>
          <li>You can access, correct, or delete your information.</li>
          <li>Health information you upload is governed by Part Two — Consumer Health Data Notice below.</li>
        </ul>
      </Callout>
      <section>
        <h2>Part One — General Privacy Policy</h2>
        <h3>Who we are and what this covers</h3>
        <p>
          This Privacy Policy explains how Vital Software Inc. ("Vital", "we") handles personal information collected
          through{' '}
          <a className="underline" href="https://vit.al">
            https://vit.al
          </a>
          , and the Vital Care Finder application inside third-party AI assistants such as Claude and ChatGPT.
        </p>
        <p>
          Health information you upload is also governed by Part Two — Consumer Health Data Notice below. Where Part One
          and Part Two differ with respect to consumer health data, Part Two controls.
        </p>

        <h3>Information we collect</h3>
        <ul>
          <li>
            <strong>Contact information</strong> — such as your name, phone number, and email, primarily so a provider
            can return your callback request.
          </li>
          <li>
            <strong>Search and request details</strong> — the specialty, location, insurance type, symptoms or
            conditions, and other inputs you provide to find a provider, and the callback or appointment requests you
            submit.
          </li>
          <li>
            <strong>Information you upload</strong> — medical documents, lab results, and photos you choose to submit
            through Atlas. This is consumer health data and is described in detail in Part Two.
          </li>
          <li>
            <strong>Device and usage information</strong> — limited technical information such as browser type, general
            location derived from IP for functionality, and pages viewed. We do not use device GPS, precise geolocation,
            Bluetooth, or similar signals to detect your proximity to a health facility.
          </li>
          <li>
            <strong>Information from the AI assistant</strong> — when you use Vital inside Claude or ChatGPT, we receive
            the relevant content of your request from that platform so we can respond.
          </li>
        </ul>

        <h3>How we use information</h3>
        <p>
          We use information to provide the Services: to return relevant provider matches, to submit your callback or
          appointment requests to providers you choose, to operate and secure the Services, to comply with law, and to
          communicate with you about your requests. We use the minimum information necessary for each purpose.
        </p>

        <h3>Cookies and analytics</h3>
        <p>
          We use Google Analytics to measure basic traffic — how many people visit, which pages they reach, and where
          they arrived from — so that we can improve the site. It sets first-party cookies in your browser so that
          several pages viewed in a row count as one visit rather than several.
        </p>
        <p>
          You can block these cookies with your browser's cookie controls or with Google's own opt-out browser add-on;
          nothing on the site depends on them.
        </p>

        <h3>No advertising or analytics trackers on health pages</h3>
        <p>
          We do not place advertising pixels or marketing SDKs anywhere on the Services, and we do not place any
          advertising or analytics tracker — including the Google Analytics measurement described above — on pages where
          you upload or view health information. We do not share health information with advertising or analytics
          platforms. We do not use third-party software development kits that transmit health data.
        </p>

        <h3>How we share information</h3>
        <ul>
          <li>
            <strong>With providers and clinics you choose</strong> — to fulfil a callback or appointment request, and
            (for health information) only after you give consent described in Part Two.
          </li>
          <li>
            <strong>With service providers (processors)</strong> under contract.
          </li>
          <li>
            <strong>For legal and safety reasons</strong> — to comply with law, enforce our terms, or protect rights and
            safety.
          </li>
        </ul>
        <p>
          We do not sell personal information, and we do not sell consumer health data in any form. We do not 'share'
          personal information for cross-context behavioural advertising.
        </p>

        <h3>AI processing</h3>
        <p>
          Vital may use AI systems and automated tools to support provider matching, extraction, summarization, routing,
          and user assistance. These tools are used for healthcare navigation and information purposes, not to provide
          diagnosis or treatment.
        </p>
        <p>
          We do not use consumer health data to train or fine-tune foundation AI models unless we obtain a separate,
          specific opt-in consent. We require service providers that process consumer health data for us to use it only
          as instructed by Vital and under appropriate contractual protections.
        </p>

        <h3>Health breach notification</h3>
        <p>
          If your unsecured health information held by Vital is acquired or disclosed without authorisation, we will
          notify affected individuals, and the FTC and/or media where required, within the timelines required by the FTC
          Health Breach Notification Rule (generally within 60 days) and the fastest applicable state deadline (for
          example, 30 days in Colorado and Florida, and 45 days for Washington consumer health data under MHMD).
          'Breach' includes unauthorised disclosure, not only external hacking.
        </p>

        <h3>Data retention and deletion</h3>
        <p>
          We retain personal information only for as long as reasonably necessary for the purposes described in this
          Policy, including providing the Services, completing requests, maintaining security and audit records,
          complying with legal obligations, resolving disputes, and enforcing agreements. When retention is no longer
          needed, we delete, de-identify, or aggregate the information according to applicable law and operational
          requirements.
        </p>
        <p>
          When you request deletion, we delete covered information from active systems where required by law and
          instruct applicable service providers to delete the information from their systems. Deletion from backups and
          logs may take additional time, but the information will be protected and retained only as required for
          security, legal, or operational purposes.
        </p>

        <h3>Your privacy rights</h3>
        <p>Depending on your state of residence, you may have the right to:</p>
        <ul>
          <li>Know / access the personal information we hold about you and how we use it;</li>
          <li>Correct inaccurate personal information;</li>
          <li>Delete your personal information (cascading to our processors);</li>
          <li>Portability — receive a copy in a portable format;</li>
          <li>
            Opt out of any sale or sharing for targeted advertising (note: we do not sell or share for these purposes);
            and
          </li>
          <li>Limit the use and disclosure of sensitive personal information, including health data.</li>
        </ul>
        <p>
          <strong>Global Privacy Control (GPC).</strong> Where required by law, we honor Global Privacy Control and
          similar legally recognized browser opt-out signals as an opt-out of sale or sharing for targeted advertising.
          Because Vital does not sell consumer health data or use it for targeted advertising, this mainly applies to
          any non-health-data processing that may be subject to those laws.
        </p>
        <p>
          <strong>How to exercise your rights.</strong> Contact us at {email}. We will verify your request and respond
          within the timeframe required by your state's law. You may use an authorised agent where the law allows. If we
          decline a request, you may appeal by contacting {email} (Attn: Privacy — Appeals).
        </p>
        <p>
          <strong>Non-discrimination.</strong> We will not deny you service or charge you differently for exercising
          your rights.
        </p>

        <h3>Children's privacy (COPPA)</h3>
        <p>
          The Services are intended for adults. We do not knowingly collect personal information from children under 13
          without required parental consent. If you believe a child has provided information without required consent,
          contact us and we will take appropriate steps.
        </p>

        <h3>Security</h3>
        <p>
          We use administrative, technical, and physical safeguards designed to protect personal information. No system
          is perfectly secure. You should use caution when submitting information online, including through third-party
          AI assistant platforms.
        </p>
      </section>
      <section>
        <h2>Part Two — Consumer Health Data Notice</h2>
        <h3>Why this notice exists</h3>
        <p>
          When you upload medical information to Vital directly (rather than through your hospital or clinic), Vital is
          acting as a direct-to-consumer personal health record provider, not as a HIPAA business associate. Your
          information is protected by consumer health data privacy laws rather than HIPAA. This notice explains what
          consumer health data we collect, how we use it, who we share it with, and the rights and consents that apply.
        </p>
        <h3>Consumer health data we collect</h3>
        <ul>
          <li>
            Health-related search inputs, such as symptoms, conditions, specialty needs, reason for visit, insurance
            type, location, and care preferences.
          </li>
          <li>Information contained in documents, images, photos, or other content you submit.</li>
          <li>
            Information extracted or inferred from submitted materials, such as medical conditions, procedures,
            medications, lab values, specialties, or care needs.
          </li>
          <li>Provider matching and navigation outputs generated from health-related information.</li>
          <li>Consent records and records of disclosures to providers or service providers.</li>
        </ul>
        <h3>Sources of consumer health data</h3>
        <p>
          We collect consumer health data directly from you, from information you submit through an AI assistant or
          MCP-compatible platform, from materials you provide, and from derived or inferred information created to
          provide the Services.
        </p>
        <h3>How we use consumer health data</h3>
        <p>
          We collect and process consumer health data only as needed to provide the services you request and for the
          purposes described in this Notice. This may include using consumer health data to:
        </p>
        <ul>
          <li>provide provider search, matching, comparison, and callback request services;</li>
          <li>extract limited information needed to support healthcare navigation and provider matching;</li>
          <li>generate informational summaries or explanations to help you understand provider options;</li>
          <li>transmit request information to a provider or clinic after any required consent;</li>
          <li>
            operate, secure, debug, support, and improve the Services in a manner consistent with this Notice; and
          </li>
          <li>comply with law, enforce our terms, protect safety, and maintain required records.</li>
        </ul>
        <p>
          We do not use consumer health data to train or fine-tune AI models unless you give separate, specific opt-in
          consent.
        </p>
        <p>We do not sell consumer health data to clinics, data brokers, advertisers, or any other third party.</p>
        <p>
          We may use automated processing to help match you with providers. This is a healthcare navigation and
          information-retrieval function. It does not provide a medical diagnosis, recommend a course of treatment, or
          replace the judgment of a licensed healthcare professional.
        </p>
        <h3>How we disclose consumer health data</h3>
        <ul>
          <li>
            Providers and clinics you choose or match with, when you request outreach or appointment help and provide
            any required sharing consent.
          </li>
          <li>
            Service providers and processors under contract, such as hosting, storage, AI processing, fax, SMS,
            telephony, security, and support vendors.
          </li>
          <li>
            Legal and safety recipients, when required by law or necessary to protect rights, safety, or security.
          </li>
        </ul>

        <h3>Consent and withdrawal</h3>
        <p>
          We obtain consent where required before collecting, using, or sharing consumer health data. We use separate
          consent for sharing consumer health data with a provider or clinic when required by law.
        </p>
        <p>
          You may withdraw consent at any time by contacting {email}. Withdrawal will not affect processing that
          occurred before withdrawal, but we will stop future processing covered by the withdrawn consent unless another
          legal basis applies.
        </p>
        <h3>Sensitive categories</h3>
        <p>
          Consumer health data may include sensitive categories, such as reproductive or sexual health information,
          genetic information, mental health information, or substance use disorder information. Where applicable law
          requires separate consent or authorization for these categories, Vital will request it before the relevant
          collection, use, or disclosure.
        </p>

        <h3>Substance use disorder records</h3>
        <p>
          If information appears to include substance use disorder records subject to 42 CFR Part 2 and you ask us to
          disclose it to a provider, Vital will apply any required separate consent and include any required
          prohibition-on-redisclosure notice.
        </p>

        <h3>No sale, no targeted advertising, and no geofencing</h3>
        <ul>
          <li>We do not sell consumer health data.</li>
          <li>We do not use consumer health data for targeted advertising or cross-context behavioral advertising.</li>
          <li>
            We do not use geofencing around healthcare facilities to identify, track, collect data from, or send
            targeted content to consumers based on proximity to a healthcare facility.
          </li>
        </ul>

        <h3>Your rights over consumer health data</h3>
        <p>You have the right to:</p>
        <ul>
          <li>Access or confirm whether we process your consumer health data.</li>
          <li>Correct inaccurate consumer health data.</li>
          <li>Delete consumer health data, subject to legal and operational limits.</li>
          <li>Withdraw consent where processing is based on consent.</li>
          <li>Receive a portable copy of covered data where required by law.</li>
          <li>
            Receive information about third parties or affiliates with whom consumer health data has been shared where
            required by law.
          </li>
          <li>Appeal a denied request where required by law.</li>
        </ul>

        <h3>How to exercise rights</h3>
        <p>Contact {email}. We will respond within the timeframe required by applicable law.</p>

        <h3>Health breach notification</h3>
        <p>
          If a breach of unsecured health information occurs and we are required to provide notice under the FTC Health
          Breach Notification Rule or state consumer health data laws, we will notify affected individuals, regulators,
          and media where required and within applicable timeframes.
        </p>

        <h3>State-specific rights</h3>
        <p>
          Residents of certain states, including Washington, Nevada, Connecticut, California, Maryland, Virginia,
          Oregon, Colorado, and others, may have additional consumer health data or sensitive data rights. Where a state
          law grants greater protection, Vital will apply that protection as required.
        </p>

        <h3>Changes and contact</h3>
        <p>We may update this policy and will post the new effective date. Questions or requests:</p>
        <p>
          Vital Software Inc.
          <br />
          Privacy: {email}
        </p>
      </section>
    </>
  )
}
